import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const parseFeatures = (text) => text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [title, text2] = l.split("|").map((s) => (s || "").trim());
  return { title, text: text2 || "" };
});

export default function AdminPlatform() {
  const [landing, setLanding] = useState(null);
  const [smtp, setSmtp] = useState({ host: "", port: 587, username: "", password: "", from_email: "", from_name: "" });
  const [featuresText, setFeaturesText] = useState("");
  const [landingUrl, setLandingUrl] = useState("");
  const [admins, setAdmins] = useState([]);
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "" });
  const [mapsKey, setMapsKey] = useState("");
  const [mapsStatus, setMapsStatus] = useState({ configured: false, key_last4: "" });
  const [resetForm, setResetForm] = useState({ email: "", password: "" });
  const [salesCodes, setSalesCodes] = useState([]);
  const [codeForm, setCodeForm] = useState({ code: "", note: "", max_uses: 50 });

  const L0 = landing || {};

  const addParagraph = () => setLanding((L) => ({ ...(L || {}), paragraphs: [...((L || {}).paragraphs || []), { title: "", body: "" }] }));
  const setParagraph = (i, patch) => setLanding((L) => ({ ...L, paragraphs: L.paragraphs.map((p, x) => (x === i ? { ...p, ...patch } : p)) }));
  const removeParagraph = (i) => setLanding((L) => ({ ...L, paragraphs: L.paragraphs.filter((_, x) => x !== i) }));
  const addLandingImage = () => {
    if (!landingUrl.trim()) return;
    setLanding((L) => ({ ...(L || {}), gallery: [...((L || {}).gallery || []), landingUrl.trim()] }));
    setLandingUrl("");
  };
  const removeLandingImage = (i) => setLanding((L) => ({ ...L, gallery: L.gallery.filter((_, x) => x !== i) }));
  const uploadLandingImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/platform/upload", fd);
      setLanding((L) => ({ ...(L || {}), gallery: [...((L || {}).gallery || []), data.url] }));
      toast.success("Uploaded — Publish homepage to make it live");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const loadAdmins = () => api.get("/platform/admins").then((r) => setAdmins(r.data)).catch(() => {});
  useEffect(() => { loadAdmins(); }, []);
  const addAdmin = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/admins", adminForm);
      toast.success("Super admin added");
      setAdminForm({ name: "", email: "", password: "" });
      loadAdmins();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const removeAdmin = async (a) => {
    try {
      await api.delete(`/platform/admins/${a.id}`);
      loadAdmins();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = () => api.get("/platform/settings").then((r) => {
    const l = r.data.landing || {};
    setLanding({ badge: "", title: "", subtitle: "", cta: "", footer: "", login_brand: "", login_tagline: "", paragraphs: [], gallery: [], ...l, show_businesses: l.show_businesses !== false });
    setFeaturesText((l.features || []).map((f) => `${f.title} | ${f.text}`).join("\n"));
    setSmtp({ host: "", port: 587, username: "", password: "", from_email: "", from_name: "", ...(r.data.smtp || {}), password: "" });
    setMapsStatus(r.data.maps || { configured: false, key_last4: "" });
    api.get("/platform/sales-codes").then((res) => setSalesCodes(res.data)).catch(() => {});
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const saveLanding = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/platform/settings", { landing: { ...landing, features: parseFeatures(featuresText) } });
      toast.success("Homepage content published");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/platform/settings", { smtp: { ...smtp, port: Number(smtp.port) } });
      toast.success("SMTP settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!landing) return <div className="py-20 text-center text-sm text-muted-foreground" data-testid="platform-loading">Loading platform settings…</div>;

  return (
    <div data-testid="admin-platform-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Platform Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Homepage content (CMS) and outbound email configuration.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <form onSubmit={saveLanding} className="space-y-4 border border-border bg-white p-6" data-testid="landing-cms-form">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Homepage content</p>
          <div className="space-y-1.5"><Label>Hero badge</Label><Input data-testid="landing-badge-input" value={landing.badge} onChange={(e) => setLanding({ ...landing, badge: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Hero title</Label><Input data-testid="landing-title-input" value={landing.title} onChange={(e) => setLanding({ ...landing, title: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Hero subtitle</Label><Textarea data-testid="landing-subtitle-input" rows={3} value={landing.subtitle} onChange={(e) => setLanding({ ...landing, subtitle: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>CTA button text</Label><Input data-testid="landing-cta-input" value={landing.cta} onChange={(e) => setLanding({ ...landing, cta: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Footer text</Label><Input data-testid="landing-footer-input" value={landing.footer} onChange={(e) => setLanding({ ...landing, footer: e.target.value })} /></div>
          </div>
          <div className="flex items-center justify-between border border-border px-3 py-2.5">
            <div>
              <Label className="text-sm">Feature businesses on homepage</Label>
              <p className="text-xs text-muted-foreground">Shows the "Businesses on CateringDiary" directory section</p>
            </div>
            <Switch data-testid="show-businesses-switch" checked={landing.show_businesses} onCheckedChange={(v) => setLanding({ ...landing, show_businesses: v })} />
          </div>
          <div className="space-y-1.5">
            <Label>Feature cards — one per line: Title | description</Label>
            <Textarea data-testid="landing-features-input" rows={6} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} />
          </div>
          <Button data-testid="landing-save-button" type="submit" className="rounded-md bg-ink hover:bg-ink-light"><Save className="mr-2 h-4 w-4" /> Publish homepage</Button>
        </form>

        <div className="h-fit space-y-4 border border-border bg-white p-6" data-testid="login-cms-card">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Login page</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Brand name</Label><Input data-testid="login-brand-input" value={L0.login_brand || ""} placeholder="CateringDiary" onChange={(e) => setLanding({ ...L0, login_brand: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Tagline</Label><Input data-testid="login-tagline-input" value={L0.login_tagline || ""} placeholder="Multi-Unit Ops Command" onChange={(e) => setLanding({ ...L0, login_tagline: e.target.value })} /></div>
          </div>
          <p className="text-xs text-muted-foreground">Saved with &ldquo;Publish homepage&rdquo;.</p>
        </div>

        <div className="h-fit space-y-4 border border-border bg-white p-6" data-testid="landing-gallery-card">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Homepage gallery (product screenshots)</p>
          <div className="flex gap-2">
            <label className="flex shrink-0 cursor-pointer items-center gap-2 border border-dashed border-border px-3 py-2 text-sm font-semibold hover:bg-muted" data-testid="landing-upload-label">
              Upload <input type="file" accept="image/*" className="hidden" data-testid="landing-upload-input" onChange={uploadLandingImage} />
            </label>
            <Input placeholder="…or paste image URL" data-testid="landing-gallery-url" value={landingUrl} onChange={(e) => setLandingUrl(e.target.value)} />
            <Button type="button" size="sm" variant="outline" className="rounded-md" data-testid="landing-gallery-add" onClick={addLandingImage}>Add</Button>
          </div>
          <div className="space-y-2">
            {(L0.gallery || []).map((url, i) => (
              <div key={i} className="flex items-center gap-2 border border-border p-2" data-testid={`landing-gallery-item-${i}`}>
                <img src={url} alt="" className="h-10 w-14 shrink-0 object-cover" />
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{url}</span>
                <button type="button" data-testid={`landing-gallery-del-${i}`} onClick={() => removeLandingImage(i)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {(L0.gallery || []).length === 0 && <p className="text-xs text-muted-foreground">No screenshots yet.</p>}
          </div>
        </div>

        <div className="h-fit space-y-4 border border-border bg-white p-6" data-testid="paragraphs-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Paragraph boxes</p>
            <Button type="button" size="sm" variant="outline" className="rounded-md" data-testid="paragraph-add" onClick={addParagraph}>Add box</Button>
          </div>
          {(L0.paragraphs || []).map((p, i) => (
            <div key={i} className="space-y-2 border border-border p-3" data-testid={`paragraph-row-${i}`}>
              <div className="flex gap-2">
                <Input placeholder="Heading (optional)" data-testid={`paragraph-title-${i}`} value={p.title} onChange={(e) => setParagraph(i, { title: e.target.value })} />
                <Button type="button" size="sm" variant="outline" className="rounded-md" data-testid={`paragraph-del-${i}`} onClick={() => removeParagraph(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
              <Textarea rows={3} placeholder="Text…" data-testid={`paragraph-body-${i}`} value={p.body} onChange={(e) => setParagraph(i, { body: e.target.value })} />
            </div>
          ))}
          {(L0.paragraphs || []).length === 0 && <p className="text-xs text-muted-foreground">No paragraph boxes yet.</p>}
        </div>

        <div className="h-fit space-y-4 border border-border bg-white p-6" data-testid="admins-card">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Platform super admins</p>
          <div className="space-y-1.5">
            {admins.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-border px-3 py-2 text-sm" data-testid={`admin-row-${a.id}`}>
                <span className="font-medium">{a.name} <span className="text-xs text-muted-foreground">({a.email})</span></span>
                <Button type="button" size="sm" variant="outline" className="rounded-md" data-testid={`admin-remove-${a.id}`} onClick={() => removeAdmin(a)}>Remove</Button>
              </div>
            ))}
          </div>
          <form onSubmit={addAdmin} className="space-y-2 border-t border-border pt-3" data-testid="admin-add-form">
            <Input placeholder="Name" required data-testid="admin-name-input" value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} />
            <Input placeholder="Email" type="email" required data-testid="admin-email-input" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} />
            <Input placeholder="Password (min 8 chars)" type="password" required minLength={8} data-testid="admin-password-input" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} />
            <Button type="submit" size="sm" data-testid="admin-add-button" className="rounded-md bg-ink hover:bg-ink-light">Add super admin</Button>
          </form>
        </div>

        <form onSubmit={saveSmtp} className="h-fit space-y-4 border border-border bg-white p-6" data-testid="smtp-form">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">SMTP (outbound email)</p>
          <p className="text-xs text-muted-foreground">Used for RFQ emails and notifications when email dispatch is enabled. Stored per platform; leave blank to keep email off.</p>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Host</Label><Input data-testid="smtp-host-input" placeholder="smtp.sendgrid.net" value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Port</Label><Input data-testid="smtp-port-input" type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Username</Label><Input data-testid="smtp-username-input" value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input data-testid="smtp-password-input" type="password" placeholder="••••••••" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>From email</Label><Input data-testid="smtp-from-email-input" placeholder="no-reply@cateringdiary.app" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>From name</Label><Input data-testid="smtp-from-name-input" placeholder="CateringDiary" value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} /></div>
          </div>
          <Button data-testid="smtp-save-button" type="submit" className="rounded-md bg-ink hover:bg-ink-light"><Save className="mr-2 h-4 w-4" /> Save SMTP</Button>
        </form>

        <form onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api.patch("/platform/settings", { maps: { google_maps_api_key: mapsKey } });
            toast.success("Google Maps key saved — road distances now active for quotes");
            setMapsKey("");
            load();
          } catch (err) {
            toast.error(formatApiError(err));
          }
        }} className="h-fit space-y-4 border border-border bg-white p-6" data-testid="maps-form">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Google Maps (road distances)</p>
          <p className="text-xs text-muted-foreground">Optional. With a key, quote travel charges use real road miles (Distance Matrix); without one, straight-line estimates are used.</p>
          {mapsStatus.configured && <p className="text-xs font-medium text-emerald-700" data-testid="maps-status">Key active ····{mapsStatus.key_last4}</p>}
          <div className="space-y-1.5"><Label>API key</Label><Input data-testid="maps-key-input" type="password" placeholder="AIza…" value={mapsKey} onChange={(e) => setMapsKey(e.target.value)} /></div>
          <Button data-testid="maps-save-button" type="submit" disabled={!mapsKey.trim()} className="rounded-md bg-ink hover:bg-ink-light"><Save className="mr-2 h-4 w-4" /> Save key</Button>
        </form>

        <form onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api.post("/platform/users/reset-password", resetForm);
            toast.success(`Password reset for ${resetForm.email}`);
            setResetForm({ email: "", password: "" });
          } catch (err) {
            toast.error(formatApiError(err));
          }
        }} className="h-fit space-y-4 border border-border bg-white p-6" data-testid="reset-password-form">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Reset a user's password</p>
          <p className="text-xs text-muted-foreground">Set a new password for any business admin, staff member or fellow super-admin.</p>
          <div className="space-y-1.5"><Label>User email</Label><Input data-testid="reset-email-input" type="email" required value={resetForm.email} onChange={(e) => setResetForm({ ...resetForm, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>New password (min 8 chars)</Label><Input data-testid="reset-password-input" type="password" required minLength={8} value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} /></div>
          <Button data-testid="reset-password-button" type="submit" className="rounded-md bg-ink hover:bg-ink-light">Reset password</Button>
        </form>

        <div className="h-fit space-y-4 border border-border bg-white p-6" data-testid="sales-codes-card">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Sales team codes</p>
          <p className="text-xs text-muted-foreground">Hand these to prospective clients. Signup with a valid code gets a 14-day full-access trial pre-filled with demo data.</p>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              const r = await api.post("/platform/sales-codes", codeForm);
              toast.success(`Code created: ${r.data.code}`);
              setCodeForm({ code: "", note: "", max_uses: 50 });
              load();
            } catch (err) {
              toast.error(formatApiError(err));
            }
          }} className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5"><Label>Code (blank = auto)</Label><Input data-testid="sales-code-input" value={codeForm.code} onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value.toUpperCase() })} placeholder="CD-…" className="w-32" /></div>
            <div className="space-y-1.5"><Label>Max uses</Label><Input data-testid="sales-code-max-uses" type="number" min="1" className="w-24" value={codeForm.max_uses} onChange={(e) => setCodeForm({ ...codeForm, max_uses: Number(e.target.value) })} /></div>
            <div className="min-w-0 flex-1 space-y-1.5"><Label>Note</Label><Input data-testid="sales-code-note" value={codeForm.note} onChange={(e) => setCodeForm({ ...codeForm, note: e.target.value })} placeholder="e.g. Spring expo" /></div>
            <Button data-testid="sales-code-create" type="submit" className="rounded-md bg-ink hover:bg-ink-light">Create code</Button>
          </form>
          {salesCodes.length === 0 && <p className="text-xs text-muted-foreground" data-testid="sales-codes-empty">No codes yet.</p>}
          {salesCodes.map((sc) => (
            <div key={sc.id} className="flex flex-wrap items-center justify-between gap-2 border border-border px-3 py-2 text-sm" data-testid={`sales-code-row-${sc.code}`}>
              <span className="font-mono font-semibold">{sc.code}</span>
              <span className="text-xs text-muted-foreground">{sc.note || "—"} · {sc.uses}/{sc.max_uses} used</span>
              <Button type="button" size="sm" variant="outline" data-testid={`sales-code-toggle-${sc.code}`} className="rounded-md"
                onClick={async () => { await api.patch(`/platform/sales-codes/${sc.code}`, { active: !sc.active }); load(); }}>
                {sc.active ? "Deactivate" : "Reactivate"}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
